'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function ContactInfoItem({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div className="flex gap-3 mb-6 items-start">
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-1 font-[Montserrat,sans-serif]">
          {label}
        </p>
        {href ? (
          <a href={href} className="text-sm text-gray-700 no-underline font-[Montserrat,sans-serif] break-all">
            {value}
          </a>
        ) : (
          <p className="text-sm text-gray-700 m-0 font-[Montserrat,sans-serif]">{value}</p>
        )}
      </div>
    </div>
  );
}

export default function ContactPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const f = document.createElement('iframe');
    const ifrmSrc =
      'https://forms.zohopublic.com/onlineinteractive1/form/ContactBrickwell/formperma/7ujaPerUXTBkb0pSBusbLwCWHMeg2UmJqRL9burt-JA?zf_rszfm=1';

    f.src = ifrmSrc;
    f.style.border = 'none';
    f.style.height = '914px';
    f.style.width = '100%';
    f.style.transition = 'all 0.5s ease';
    f.setAttribute('aria-label', 'Contact Brickwell');

    container.appendChild(f);

    const messageHandler = (event: MessageEvent) => {
      const evntData = event.data;
      if (evntData && typeof evntData === 'string') {
        const zf_ifrm_data = evntData.split('|');
        if (zf_ifrm_data.length === 2 || zf_ifrm_data.length === 3) {
          const zf_perma = zf_ifrm_data[0];
          const zf_ifrm_ht_nw = parseInt(zf_ifrm_data[1], 10) + 15 + 'px';
          const iframe = container.getElementsByTagName('iframe')[0];
          if (iframe && iframe.src.indexOf('formperma') > 0 && iframe.src.indexOf(zf_perma) > 0) {
            const prevIframeHeight = iframe.style.height;
            const zf_tout = zf_ifrm_data.length === 3;
            if (zf_tout) iframe.scrollIntoView();
            if (prevIframeHeight !== zf_ifrm_ht_nw) {
              if (zf_tout) {
                setTimeout(() => { iframe.style.height = zf_ifrm_ht_nw; }, 500);
              } else {
                iframe.style.height = zf_ifrm_ht_nw;
              }
            }
          }
        }
      }
    };

    window.addEventListener('message', messageHandler, false);

    return () => {
      window.removeEventListener('message', messageHandler, false);
      if (container.contains(f)) container.removeChild(f);
    };
  }, []);

  return (
    <main className="font-[Montserrat,sans-serif]">
      <Header />

      {/* Hero */}
      <section className="bg-[#003c82] px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12 sm:pb-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-semibold tracking-[0.14em] uppercase text-[#6dcff6] mb-4">
            Get In Touch
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white m-0 leading-tight">
            Contact Us
          </h1>
          <p className="text-sm sm:text-base text-white/65 mt-4 mb-0 max-w-xl">
            Have a question about housing, partnerships, or investment opportunities? We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">

          {/* Contact Info */}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-[#003c82] mt-0 mb-7">
              Reach Out Directly
            </h2>

            <ContactInfoItem
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#003c82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              }
              label="Corporate Headquarters"
              value="667 Madison Ave, New York, NY 10065"
            />

            <ContactInfoItem
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#003c82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              }
              label="Email"
              value="info@brickwellhousing.com"
              href="mailto:info@brickwellhousing.com"
            />

            <div className="mt-10 p-5 sm:p-6 bg-[#f0f5fc] border-l-4 border-[#003c82]">
              <p className="text-xs font-semibold text-[#003c82] m-0 mb-2 uppercase tracking-[0.08em]">
                Response Time
              </p>
              <p className="text-sm text-gray-600 m-0 leading-relaxed">
                We typically respond to all inquiries within 1–2 hours.
              </p>
            </div>
          </div>

          {/* Zoho Form */}
          <div className="w-full min-w-0">
            <div
              id="zf_div_7ujaPerUXTBkb0pSBusbLwCWHMeg2UmJqRL9burt-JA"
              ref={containerRef}
              className="w-full"
            />
          </div>

        </div>
      </section>

      <Footer />
    </main>
  );
}